import React from "react";

function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="bg-white shadow-sm rounded-lg p-6 sm:p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">PRIVACY POLICY</h1>
        <p className="text-xl text-gray-700 mb-6">TradeEngine.AI Pte. Ltd.</p>
        
        <div className="flex gap-4 text-sm text-gray-600 mb-8">
          <p><strong>Effective Date:</strong> 01/01/2026</p>
          <p><strong>Last Updated:</strong> 01/01/2026</p>
        </div>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. INTRODUCTION</h2>
          <p className="text-gray-700 mb-4">
            Welcome to TradeEngine.AI ("<strong>TradeEngine</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>"). We are committed to protecting your privacy and ensuring the security of your information.
          </p>
          <p className="text-gray-700 mb-4">
            This Privacy Policy explains how TradeEngine.AI Pte. Ltd., a company incorporated in Singapore (Company Registration No. [Insert Number]), collects, uses, discloses, and protects information when you use our AI-powered trade finance platform and low-code/no-code development services (collectively, the "<strong>Services</strong>").
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.1 Scope of This Policy</h3>
          <p className="text-gray-700 mb-2">This Privacy Policy applies to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Our website at <a href="http://www.tradeengine.ai" className="text-blue-600 hover:underline">www.tradeengine.ai</a> (the "<strong>Website</strong>")</li>
            <li>Our cloud-based SaaS platform and applications</li>
            <li>Our APIs and integration services</li>
            <li>Our mobile applications (if applicable)</li>
            <li>All related services, features, and functionalities</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Who We Serve</h2>
          <p className="text-gray-700 mb-4">
            TradeEngine.AI serves both business-to-business (B2B) and business-to-consumer (B2C) markets.
          </p>
          <p className="text-gray-700 mb-4">
            Our platform primarily enables trade-finance organizations to automate workflows, manage data, and leverage AI-driven analytics. In doing so, we process information in multiple capacities depending on the user type and contractual framework. Accordingly, this Policy governs the handling of:
          </p>
          
          <div className="space-y-4 mb-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">1. Business Customer Information (B2B):</h4>
              <p className="text-gray-700">
                This includes information relating to corporate subscribers, institutional clients, financial entities, and other organizations that procure and use our Services ("Business Customers"). We also process information about their personnel, including employees, representatives, contractors, account administrators, and other authorized users who access the Services on behalf of the Business Customer.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">2. Individual Consumer Information (B2C):</h4>
              <p className="text-gray-700">
                This includes information about individual users who directly subscribe to or interact with TradeEngine.AI's Services for personal or professional use without acting on behalf of a corporate entity.
              </p>
            </div>
          </div>

          <p className="text-gray-700 mb-4">
            By structuring the Policy to address both B2B and B2C contexts, we ensure clarity regarding the categories of data processed, the applicable legal bases, and the respective rights and obligations under relevant data protection laws.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.2 Your Consent</h3>
          <p className="text-gray-700">
            By accessing or using our Services, you acknowledge that you have read, understood, and agree to this Privacy Policy. If you do not agree, please discontinue use of our Services immediately.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. INFORMATION WE COLLECT</h2>
          <p className="text-gray-700 mb-4">
            We collect various types of information to provide, maintain, improve, and secure our Services.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.1 Account and Business Information</h3>
          <p className="text-gray-700 mb-2">When you or your organization registers for our Services, we collect:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Business Details:</strong> Company name, business registration number, tax identification number, business type, industry sector</li>
            <li><strong>Contact Information:</strong> Business address, phone numbers, email addresses</li>
            <li><strong>Account Credentials:</strong> Username, password (encrypted), security questions</li>
            <li><strong>Authorized User Information:</strong> Names, job titles, email addresses, phone numbers, and role assignments of individuals authorized to access the platform</li>
            <li><strong>Billing Information:</strong> Billing address, payment method details (processed by third-party payment processors)</li>
            <li><strong>Verification Documents:</strong> Business registration certificates, certificates of incorporation, proof of business address</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.2 Trade Finance Data</h3>
          <p className="text-gray-700 mb-2">In providing our trade finance services, we process:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Transaction Documents:</strong> Invoices, purchase orders, bills of lading, packing lists, certificates of origin, insurance documents, inspection certificates</li>
            <li><strong>Financial Information:</strong> Bank account details, credit terms, payment histories, financing requests, credit assessments</li>
            <li><strong>Party Information:</strong> Details about buyers, suppliers, logistics providers, banks, and other transaction participants</li>
            <li><strong>Transaction Metadata:</strong> Transaction values, currencies, payment terms, delivery terms, shipment details</li>
            <li><strong>Supporting Documentation:</strong> Contracts, agreements, correspondence related to trade transactions</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.3 Financial and Credit Information</h3>
          <p className="text-gray-700 mb-2">For risk assessment and financing services, we may collect and process:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Banking Information:</strong> Bank account numbers, SWIFT/BIC codes, account statements, transaction histories</li>
            <li><strong>Credit Information:</strong> Credit scores, credit reports, payment histories, outstanding obligations</li>
            <li><strong>Financial Statements:</strong> Balance sheets, profit and loss statements, cash flow statements</li>
            <li><strong>Company Financial Health Indicators:</strong> Revenue data, debt ratios, liquidity metrics</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.4 Usage and Technical Data</h3>
          <p className="text-gray-700 mb-2">We automatically collect information about how you interact with our Services:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Device Information:</strong> IP address, device type, operating system, browser type and version, device identifiers</li>
            <li><strong>Log Data:</strong> Access times, pages viewed, features used, clicks, navigation paths</li>
            <li><strong>Performance Data:</strong> Response times, download errors, page load times, system performance metrics</li>
            <li><strong>Location Data:</strong> Geographic location based on IP address</li>
            <li><strong>Session Information:</strong> Login times, session duration, session identifiers</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.5 AI Interaction Data</h3>
          <p className="text-gray-700 mb-2">As an AI-powered platform, we collect:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>User Inputs:</strong> Queries, commands, search terms, and other inputs provided to our AI systems</li>
            <li><strong>AI Outputs:</strong> Responses, recommendations, and analyses generated by our AI models</li>
            <li><strong>Feedback Data:</strong> User ratings, corrections, and feedback on AI-generated content</li>
            <li><strong>Interaction Patterns:</strong> Frequency and types of AI feature usage, workflow configurations</li>
            <li><strong>Training Data:</strong> Aggregated and anonymized data used to improve our AI models (see Section 5 for details)</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.6 Communications</h3>
          <p className="text-gray-700 mb-2">We collect information when you communicate with us:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Support Communications:</strong> Customer service inquiries, support tickets, chat transcripts, email correspondence</li>
            <li><strong>Feedback and Surveys:</strong> Responses to surveys, product feedback, feature requests</li>
            <li><strong>Marketing Communications:</strong> Newsletter subscriptions, marketing preferences, event registrations (if applicable)</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.7 Third-Party Sources</h3>
          <p className="text-gray-700 mb-2">We may receive information from:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Financial Institutions:</strong> Banks, payment processors, credit bureaus</li>
            <li><strong>Compliance Databases:</strong> KYC/AML verification services, sanctions screening databases, watchlists</li>
            <li><strong>Business Information Providers:</strong> Company registries, business directories, credit reporting agencies</li>
            <li><strong>Integration Partners:</strong> Data from third-party systems integrated with our platform (with your authorization)</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.8 Additional Information</h3>
          <p className="text-gray-700 mb-4">
            The categories of Collection of Data described in this Section represent the primary types of information we collect in the course of delivering our Services. However, depending on your specific use of the Platform, your interactions with our features, your jurisdiction, and your organizational configuration, we may process additional information that is reasonably necessary to provide the Services, comply with legal and regulatory obligations, or fulfill legitimate business purposes. Any such processing will be carried out in accordance with this Privacy Policy, applicable data protection laws, and the permissions or authorizations you or your organization provide to us.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.9 Special Categories of Data</h3>
          <p className="text-gray-700">
            We do not intentionally collect or process Special Category or sensitive Personal Data (such as biometric identifiers, health data, racial or ethnic origin, political opinions, religious or philosophical beliefs, union membership, sexual orientation, or genetic data), unless such processing is strictly required for regulatory compliance or expressly permitted by applicable law and supported by your explicit consent where necessary.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. HOW WE USE INFORMATION</h2>
          <p className="text-gray-700 mb-4">
            We use the information we collect for the following purposes:
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.1 Service Provision and Platform Operations</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Deliver Services:</strong> Provide access to and functionality of our trade finance platform</li>
            <li><strong>Process Transactions:</strong> Facilitate trade finance transactions, document processing, and workflow automation</li>
            <li><strong>Account Management:</strong> Create and manage user accounts, authenticate users, manage permissions</li>
            <li><strong>Customer Support:</strong> Respond to inquiries, troubleshoot issues, provide technical assistance</li>
            <li><strong>Platform Customization:</strong> Configure custom workflows, low-code/no-code solutions tailored to your business needs</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.2 AI Model Training and Improvement</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Model Development:</strong> Train, test, and improve our AI algorithms and machine learning models</li>
            <li><strong>Feature Enhancement:</strong> Develop new AI-powered features and capabilities</li>
            <li><strong>Quality Assurance:</strong> Evaluate and improve the accuracy, relevance, and performance of AI outputs</li>
            <li><strong>Anonymized Analytics:</strong> Use aggregated, anonymized data to identify usage patterns and improvement opportunities</li>
          </ul>
          <p className="text-gray-700 mb-4 bg-gray-50 p-4 rounded border-l-4 border-blue-500">
            <strong>Important Note:</strong> We use aggregated and anonymized data for AI training. Personal Data is not used to train models unless it has been de-identified in accordance with applicable privacy laws.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.3 Compliance, Risk Management, and Security</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Regulatory Compliance:</strong> Comply with applicable trade finance regulations, anti-money laundering (AML) laws, counter-terrorism financing (CTF) requirements, and sanctions laws</li>
            <li><strong>KYC/AML Verification:</strong> Verify business identities, screen against sanctions lists and watchlists, conduct due diligence</li>
            <li><strong>Fraud Prevention:</strong> Detect, prevent, and investigate fraudulent transactions, unauthorized access, and security incidents</li>
            <li><strong>Risk Assessment:</strong> Evaluate credit risk, transaction risk, and counterparty risk</li>
            <li><strong>Security Monitoring:</strong> Monitor systems for security threats, vulnerabilities, and suspicious activity</li>
            <li><strong>Audit and Compliance Reporting:</strong> Maintain records for regulatory audits and examinations</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.4 Analytics and Service Improvement</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Usage Analytics:</strong> Analyze how our Services are used to improve user experience and platform performance</li>
            <li><strong>Product Development:</strong> Identify opportunities for new features, services, and improvements</li>
            <li><strong>Performance Optimization:</strong> Optimize system performance, reliability, and scalability</li>
            <li><strong>Research and Development:</strong> Conduct research to advance trade finance technology and AI capabilities</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.5 Business Operations</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Billing and Payments:</strong> Process subscription fees, invoicing, payment collection</li>
            <li><strong>Business Administration:</strong> Manage contracts, maintain business records, conduct internal operations</li>
            <li><strong>Business Continuity:</strong> Backup data, disaster recovery planning, service continuity</li>
            <li><strong>Legal Proceedings:</strong> Respond to legal requests, enforce our terms, protect our rights and property</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.6 Communications</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Transactional Communications:</strong> Send service-related notifications, account updates, security alerts</li>
            <li><strong>Platform Updates:</strong> Notify users of platform changes, new features, maintenance schedules</li>
            <li><strong>Regulatory Notices:</strong> Communicate policy changes, regulatory updates affecting our Services</li>
            <li><strong>Marketing Communications:</strong> Send newsletters, product announcements, promotional materials (with your consent, where required)</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.7 Legal Bases for Processing Personal Data</h3>
          <p className="text-gray-700 mb-4">
            We process Personal Data only when we have a lawful basis to do so under applicable data protection laws, including the EU GDPR, UK GDPR, the Singapore PDPA, and relevant U.S. state privacy laws. Depending on the context of your interaction with the Platform, we rely on one or more of the following legal bases:
          </p>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Performance of a Contract</h4>
              <p className="text-gray-700">
                We process Personal Data where it is necessary to provide our Services, operate the Platform, verify your identity, execute trade finance workflows, authenticate account access, process transactions, enable AI-supported features, and otherwise perform our contractual obligations to you or your organization.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Compliance with Legal and Regulatory Obligations</h4>
              <p className="text-gray-700">
                We process Personal Data to comply with obligations under applicable laws governing trade finance, anti-money laundering (AML), counter-terrorism financing (CTF), sanctions compliance, fraud prevention, financial reporting, recordkeeping, KYC/AML verification, and other regulatory frameworks relevant to cross-border transactions and financial services.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Legitimate Interests</h4>
              <p className="text-gray-700">
                We process Personal Data for purposes that support our legitimate business interests, provided such interests are not overridden by your rights or interests. These purposes include improving platform functionality, enhancing security, monitoring system performance, developing new features, conducting usage analytics, optimizing AI models using anonymized data, ensuring business continuity, and protecting our legal rights.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Consent (Where Required)</h4>
              <p className="text-gray-700">
                Where applicable laws require consent—such as for certain marketing communications, optional features, or specific categories of Cookies—we will obtain your explicit consent before processing such data. You may withdraw your consent at any time without affecting the lawfulness of processing carried out before the withdrawal.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Protection of Vital or Public Interests (Rare Cases)</h4>
              <p className="text-gray-700">
                In limited circumstances, we may process Personal Data where necessary to protect the vital interests of an individual or to comply with obligations in the public interest, such as preventing serious harm, fraud, or security risks. These scenarios are rare and occur only where legally justified.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. AI AND AUTOMATED DECISION-MAKING</h2>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5.1 Use of AI Technologies</h3>
          <p className="text-gray-700 mb-4">
            TradeEngine.AI uses artificial intelligence and machine-learning technologies to support and optimize certain functionalities within the Platform. These technologies may process Personal Data to enable analytics, risk alerts, document validation, fraud-prevention workflows, and other service-enhancement features.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5.2 Automated Decision-Making</h3>
          <p className="text-gray-700 mb-4">
            We may use limited forms of automated processing to generate preliminary assessments or system recommendations. These processes assist internal workflows but do not result in decisions that produce legal or similarly significant effects without human involvement. Final decisions relating to approvals, rejections, compliance determinations, or risk assessments are always subject to human review.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5.3 Your Rights Regarding Automated Processing</h3>
          <p className="text-gray-700 mb-2">
            Depending on your jurisdiction (including GDPR, UK GDPR, EU AI Act, and relevant state privacy laws), you may have the right to:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Know when your Personal Data is subject to automated processing</li>
            <li>Request human intervention in decisions supported by automated systems</li>
            <li>Contest or clarify decisions made on the basis of automated processing</li>
            <li>Receive meaningful information about the logic involved, as well as the significance and potential consequences</li>
          </ul>
          <p className="text-gray-700 mb-4">
            To exercise these rights, please contact us using the details in Section 15.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5.4 AI Training and Data Use</h3>
          <p className="text-gray-700 mb-2">
            We may use Personal Data to improve and refine AI models, subject to strict safeguards:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Anonymization and Aggregation:</strong> Personal Data used for AI training is anonymized or aggregated to prevent identification.</li>
            <li><strong>Business Customer Controls:</strong> Business customers may request to restrict anonymized data from being used for model-training purposes.</li>
            <li><strong>Data Minimization:</strong> Only the minimum data necessary for model improvement is retained or processed.</li>
            <li><strong>Security:</strong> All AI-related datasets are protected using the same technical and organizational measures applicable to operational data.</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">5.5 Fairness, Transparency, and Accountability</h3>
          <p className="text-gray-700 mb-2">
            We maintain governance measures to ensure our AI systems operate responsibly, including:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Regular evaluation of AI outputs for accuracy, fairness, and bias</li>
            <li>Documented human oversight for decisions that may impact users</li>
            <li>Clear, accessible information about the capabilities and limitations of our AI features</li>
          </ul>
          <p className="text-gray-700 bg-blue-50 p-4 rounded border-l-4 border-blue-500">
            <strong>Important:</strong> We do not make solely automated decisions that produce legal effects or similarly significantly affect you without human oversight. Critical decisions, such as final credit approvals or transaction rejections, involve human review.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. HOW WE SHARE YOUR INFORMATION</h2>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Service Providers and Sub-Processors</h3>
          <p className="text-gray-700 mb-4">
            We share Personal Data with trusted third-party service providers who support our operations, such as cloud hosting providers, analytics and security tools, communication platforms, and AI/ML infrastructure partners. These providers access data only to perform contracted services and are bound by confidentiality and data protection obligations. A current list of subprocessors is available upon request.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Financial Institution Partners</h3>
          <p className="text-gray-700 mb-4">
            Where necessary to deliver our Services, we may share information with banks, payment networks, financing partners, factoring or trade credit insurers, and other regulated financial institutions.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Compliance and Verification Services</h3>
          <p className="text-gray-700 mb-4">
            To meet regulatory obligations, we may disclose information to identity verification providers, sanctions screening tools, credit bureaus, and relevant regulatory or law-enforcement authorities.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Business Transfers</h3>
          <p className="text-gray-700 mb-4">
            If TradeEngine.AI undergoes a merger, acquisition, restructuring, or asset transfer, Personal Data may be transferred to the successor entity. We will notify you where required by law.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Legal Obligations and Rights Protection</h3>
          <p className="text-gray-700 mb-4">
            We may disclose information to comply with legal requirements, enforce our agreements, prevent fraud or security incidents, protect our rights and users, or safeguard vital interests.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">With Your Consent</h3>
          <p className="text-gray-700 mb-4">
            We may share information with third parties when you provide explicit consent, including integrations requested by you, co-marketing participation, or publication of testimonials.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Customer's Third-Party Integrations</h3>
          <p className="text-gray-700 mb-4">
            If you are an End User of a business Customer, your organization may enable additional integrations or third-party data flows. Refer to your organization's privacy notice for details.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Aggregated or De-Identified Data</h3>
          <p className="text-gray-700">
            We may share anonymized or aggregated data for industry analysis, research, product development, or reporting. This information does not identify any individual.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. INTERNATIONAL DATA TRANSFERS</h2>
          <p className="text-gray-700 mb-4">
            TradeEngine.AI is based in Singapore, and we process data globally to serve our international customer base.
          </p>
          <p className="text-gray-700 mb-4">
            TradeEngine.AI operates globally. Accordingly, Personal Data may be transferred to, stored in, or processed from jurisdictions outside your country of residence. These transfers are conducted strictly in accordance with applicable data protection laws and with safeguards designed to ensure an adequate level of protection.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">7.1 Cross-Border Processing Locations</h3>
          <p className="text-gray-700 mb-2">We process Personal Data in multiple jurisdictions, including:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Singapore</strong> – Primary processing and operations; governed by the Personal Data Protection Act (PDPA).</li>
            <li><strong>European Union/EEA</strong> – For EU-based customers and EU-hosted services.</li>
            <li><strong>United Kingdom</strong> – When serving UK-based entities or providers.</li>
            <li><strong>United States</strong> – For cloud infrastructure, security tools, and certain third-party service providers.</li>
            <li><strong>Other Jurisdictions</strong> – As required to deliver the Services, provide support, ensure resilience, or engage approved sub-processors.</li>
          </ul>
          <p className="text-gray-700 mb-4">
            Where foreign laws provide different data protection standards, we apply contractual and technical safeguards to ensure a level of protection consistent with applicable legal requirements.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">7.2 Lawful Transfer Mechanisms and Safeguards</h3>
          <p className="text-gray-700 mb-2">We rely on legally recognized transfer mechanisms, including:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Singapore PDPA-compliant transfer obligations and protection standards</li>
            <li>EU/EEA GDPR mechanisms, such as:
              <ul className="list-circle pl-6 mt-1 space-y-1">
                <li>Adequacy Decisions</li>
                <li>Standard Contractual Clauses (SCCs)</li>
                <li>Binding Corporate Rules (where applicable)</li>
                <li>Explicit consent (where legally appropriate)</li>
                <li>Transfers necessary for contract performance</li>
              </ul>
            </li>
            <li>UK GDPR mechanisms, including UK Addendum/IDTA</li>
            <li>Local transfer mechanisms in jurisdictions with specific requirements (e.g., Australia, Canada, India)</li>
          </ul>
          <p className="text-gray-700 mb-4">
            In addition, we implement strong technical and organizational measures, including encryption in transit and at rest, strict access controls, MFA, network segmentation, continuous monitoring, and data minimization practices. These measures ensure that Personal Data receives a level of protection consistent with applicable international standards.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">7.3 Customer and User Responsibilities</h3>
          <p className="text-gray-700 mb-4">
            If you access or use the Services from a jurisdiction with restrictions on international data transfers, you are responsible for ensuring that such access and transfer are permitted under your local laws. Your continued use of the Services constitutes confirmation that such cross-border transfers are lawful for you and that you have secured all required consents or authorizations.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">7.4 Transfer Documentation and Requests</h3>
          <p className="text-gray-700">
            You may request information regarding the specific transfer mechanisms and safeguards applicable to your data. We may provide such information subject to reasonable confidentiality, security, and regulatory limitations. Contact details are provided in Section 15.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. DATA SECURITY</h2>
          <p className="text-gray-700 mb-4">
            We implement comprehensive security measures to protect your information from unauthorized access, disclosure, alteration, and destruction.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">8.1 Technical Security Measures</h3>
          
          <div className="mb-4">
            <h4 className="font-semibold text-gray-900 mb-2">Encryption:</h4>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Data encrypted in transit using TLS 1.2 or higher</li>
              <li>Data encrypted at rest using AES-256 or equivalent</li>
              <li>Database encryption with industry-standard protocols</li>
            </ul>
          </div>

          <div className="mb-4">
            <h4 className="font-semibold text-gray-900 mb-2">Access Controls:</h4>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Multi-factor authentication (MFA) for platform access</li>
              <li>Role-based access control (RBAC) with principle of least privilege</li>
              <li>Unique user credentials with strong password requirements</li>
              <li>Automated session timeout and access logging</li>
            </ul>
          </div>

          <div className="mb-4">
            <h4 className="font-semibold text-gray-900 mb-2">Network Security:</h4>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Firewalls and intrusion detection/prevention systems (IDS/IPS)</li>
              <li>Network segmentation and isolation</li>
              <li>Regular security patches and updates</li>
              <li>DDoS protection and mitigation</li>
            </ul>
          </div>

          <div className="mb-4">
            <h4 className="font-semibold text-gray-900 mb-2">Application Security:</h4>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Secure software development lifecycle (SDLC)</li>
              <li>Regular code reviews and security testing</li>
              <li>Vulnerability scanning and penetration testing</li>
              <li>Web application firewall (WAF)</li>
            </ul>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">8.2 Organizational Security Measures</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Security Policies:</strong> Comprehensive information security policies and procedures</li>
            <li><strong>Employee Training:</strong> Regular security awareness training for all personnel</li>
            <li><strong>Background Checks:</strong> Screening of employees with access to sensitive data</li>
            <li><strong>Confidentiality Obligations:</strong> Contractual confidentiality requirements for all staff and contractors</li>
            <li><strong>Incident Response:</strong> Documented incident response and breach notification procedures</li>
            <li><strong>Business Continuity:</strong> Disaster recovery and business continuity plans</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">8.3 Data Segregation</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Customer data is logically segregated to prevent unauthorized cross-customer access</li>
            <li>Database-level isolation and tenant separation</li>
            <li>Secure multi-tenancy architecture</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">8.4 Security Monitoring and Auditing</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>24/7 Monitoring:</strong> Continuous monitoring of systems for security threats</li>
            <li><strong>Audit Logs:</strong> Comprehensive logging of access and activities</li>
            <li><strong>Regular Audits:</strong> Periodic internal and external security audits</li>
            <li><strong>Compliance Certifications:</strong> [Insert applicable certifications: SOC 2, ISO 27001, PCI DSS, etc.]</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">8.5 Third-Party Security</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>All service providers and subprocessors are required to maintain appropriate security measures</li>
            <li>We conduct due diligence reviews of third-party security practices</li>
            <li>Contractual obligations require third parties to notify us of any security incidents</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">8.6 Security Limitations</h3>
          <p className="text-gray-700 mb-2">
            While we implement industry-standard security measures, no system is completely secure. We cannot guarantee absolute security of your information. You are responsible for:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Maintaining confidentiality of your account credentials</li>
            <li>Implementing appropriate security measures on your devices</li>
            <li>Promptly notifying us of any suspected security breach or unauthorized access</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">8.7 Data Breach Notification</h3>
          <p className="text-gray-700 mb-2">
            In the event of a data breach that affects your Personal Data:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>We will notify you without undue delay as required by applicable law</li>
            <li>Notification will include information about the nature of the breach, potential consequences, and measures taken</li>
            <li>We will notify relevant supervisory authorities as required by law (e.g., within 72 hours under GDPR)</li>
          </ul>
          <p className="text-gray-700 mt-4">
            To report a security concern, contact us immediately at: <a href="mailto:security@tradeengine.ai" className="text-blue-600 hover:underline">security@tradeengine.ai</a>
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. DATA RETENTION</h2>
          <p className="text-gray-700 mb-4">
            We retain Personal Data only for as long as necessary to fulfil the purposes described in this Privacy Policy or to comply with legal, regulatory, and operational requirements. Retention periods are determined based on the type of data, applicable laws, potential risks, and business needs.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">General Principles</h3>
          <p className="text-gray-700 mb-4">
            Personal Data is retained only for its intended purpose unless a longer period is required by law. We regularly review our records, delete information that is no longer needed, and honour valid deletion requests where permitted.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Retention Periods</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Account & User Data:</strong> Kept for the duration of the customer relationship and up to 3 years after closure, unless a longer period is legally required.</li>
            <li><strong>Transaction & Financial Data:</strong> Retained for 5–7 years to meet audit, tax, and AML/KYC obligations.</li>
            <li><strong>Communications:</strong> Support and correspondence records kept for up to 3 years.</li>
            <li><strong>Technical Logs:</strong> Security and access logs retained for 6–12 months; anonymized analytics may be kept indefinitely.</li>
            <li><strong>AI/ML Data:</strong> De-identified training data may be retained indefinitely; user feedback retained for around 2 years.</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Backups & Archives</h3>
          <p className="text-gray-700 mb-4">
            Backups are maintained only for disaster recovery and are typically overwritten within 30–90 days. Archived compliance data follows applicable legal retention periods.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Legal Holds</h3>
          <p className="text-gray-700 mb-4">
            If required for litigation, investigations, or regulatory inquiries, we may retain Personal Data beyond standard periods until the legal hold is lifted.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Deletion</h3>
          <p className="text-gray-700">
            When retention periods expire, Personal Data is securely deleted or anonymized using methods that prevent recovery.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. YOUR RIGHTS</h2>
          <p className="text-gray-700 mb-4">
            Depending on your jurisdiction, you may have various rights regarding your Personal Data.
          </p>
          <p className="text-gray-700 mb-4">
            Your privacy rights depend on the laws of your jurisdiction. You may exercise these rights by contacting us using the details in Section 15.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">10.1 Singapore (PDPA)</h3>
          <p className="text-gray-700 mb-4">
            Individuals in Singapore have the right to access and correct their Personal Data, withdraw consent for processing (which may affect service delivery), request data portability where applicable, and opt out of direct marketing. These rights are subject to PDPA limitations and lawful exceptions.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">10.2 European Union / EEA (GDPR)</h3>
          <p className="text-gray-700 mb-4">
            EU/EEA individuals have the right to access, rectify, erase, restrict processing, object to processing (including direct marketing), request data portability, and avoid decisions based solely on automated processing. They may also lodge complaints with their local Data Protection Authority.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">10.3 Germany (BDSG)</h3>
          <p className="text-gray-700 mb-4">
            German users benefit from additional protections for employee data and further safeguards for automated individual decision-making under the BDSG.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">10.4 United States (State Privacy Laws)</h3>
          <p className="text-gray-700 mb-4">
            Depending on the state, individuals may have rights to access, delete, correct, and receive information about Personal Data collected, and to opt out of certain uses such as targeted advertising or "sale"/"sharing" of data. California residents (CCPA/CPRA) also have rights to limit use of sensitive data and protection from discrimination for exercising privacy rights. Similar rights may apply in Virginia, Colorado, Connecticut, Utah, and other states with privacy laws.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">10.5 How to Exercise Your Rights</h3>
          <p className="text-gray-700 mb-2">To exercise any of these rights:</p>
          
          <div className="space-y-3 mb-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">1. Submit a Request:</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>Email: <a href="mailto:privacy@tradeengine.ai" className="text-blue-600 hover:underline">privacy@tradeengine.ai</a></li>
                <li>Through your account settings (for certain requests)</li>
                <li>Contact our Data Protection Officer (see Section 15)</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-1">2. Verification:</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>We will verify your identity before processing requests</li>
                <li>May require additional information to confirm your identity</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-1">3. Response Timeline:</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>Singapore PDPA: Within 30 days</li>
                <li>EU GDPR: Within 1 month (extendable by 2 additional months for complex requests)</li>
                <li>US State Laws: As required by applicable law</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-1">4. No Fee:</h4>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>Generally no charge for requests</li>
                <li>We may charge a reasonable fee for manifestly unfounded, excessive, or repetitive requests</li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">10.6 Limitations and Exceptions</h3>
          <p className="text-gray-700 mb-2">Your rights may be limited or denied when:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Required by law or legal obligations</li>
            <li>Necessary to establish, exercise, or defend legal claims</li>
            <li>Conflicting with other individuals' rights and freedoms</li>
            <li>Technically impossible or disproportionately difficult to fulfill</li>
            <li>Affecting proprietary business information or trade secrets</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">10.7 Business Customer Responsibilities</h3>
          <p className="text-gray-700 mb-2">
            <strong>For End Users:</strong> Some rights may need to be exercised through your employer or organization (the Customer). Your organization is the Data Controller for certain data, and we process data on their behalf. Please consult your organization's privacy notice and contact them for certain requests.
          </p>
          <p className="text-gray-700">
            <strong>For Customers:</strong> You are responsible for responding to End User rights requests regarding data you control. We will assist you in fulfilling these obligations as a Data Processor.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. COOKIES AND TRACKING TECHNOLOGIES</h2>
          <p className="text-gray-700 mb-4">
            We use cookies and similar tracking technologies to provide and improve our Services.
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Managing Cookies:</strong> Browser settings, preference center</li>
            <li><strong>Third-Party Cookies:</strong> Listed with purposes</li>
            <li><strong>API Authentication:</strong> Notes token-based security</li>
          </ul>
          <p className="text-gray-700">
            Link to detailed Cookie Policy: <a href="#" className="text-blue-600 hover:underline">[link]</a>
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. THIRD-PARTY SERVICES AND LINKS</h2>
          <p className="text-gray-700 mb-4">
            Our platform may integrate with or contain links to third-party services, websites, and applications.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">12.1 Third-Party Integrations</h3>
          <p className="text-gray-700 mb-2">We integrate with various third-party services to enhance functionality:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li><strong>Financial Institutions:</strong> Bank APIs for payment processing and account verification</li>
            <li><strong>Logistics Providers:</strong> Shipment tracking and delivery verification services</li>
            <li><strong>ERP Systems:</strong> Integration with customer enterprise resource planning systems</li>
            <li><strong>Accounting Software:</strong> QuickBooks, Xero, SAP integrations</li>
            <li><strong>Communication Tools:</strong> Slack, Microsoft Teams integrations</li>
            <li><strong>Document Management:</strong> Google Drive, Dropbox, SharePoint integrations</li>
          </ul>

          <p className="text-gray-700 mb-2">When you authorize these integrations:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Data may be shared with third-party providers</li>
            <li>Third parties' privacy policies govern their use of your data</li>
            <li>You can revoke integration access through platform settings</li>
            <li>We are not responsible for third-party privacy practices</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">12.2 Third-Party Links</h3>
          <p className="text-gray-700 mb-2">Our Website and platform may contain links to external websites. We are not responsible for:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Privacy practices of third-party websites</li>
            <li>Content, accuracy, or availability of external sites</li>
            <li>Third-party terms of service or policies</li>
          </ul>
          <p className="text-gray-700 mb-4">
            We encourage you to review the privacy policies of any third-party sites you visit.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">12.3 Social Media Features</h3>
          <p className="text-gray-700 mb-2">
            Our Website may include social media features (e.g., LinkedIn, Twitter share buttons). These features may:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Collect your IP address and visited pages</li>
            <li>Set cookies to enable proper functionality</li>
            <li>Be governed by the privacy policy of the social media company</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">12.4 Third-Party Data Collection</h3>
          <p className="text-gray-700 mb-4">
            We do not control third-party data collection practices. Third parties may collect information about your online activities over time and across different websites.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">12.5 Due Diligence</h3>
          <p className="text-gray-700">
            We conduct reasonable due diligence on third-party service providers, but we cannot guarantee their privacy or security practices. Integration with third-party services is at your discretion and risk.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. BUSINESS CUSTOMER RESPONSIBILITIES</h2>
          <p className="text-gray-700 mb-4">
            This section applies to business organizations using TradeEngine.AI ("Customers").
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">13.1 Roles of the Parties</h3>
          <p className="text-gray-700 mb-4">
            Customers act as Data Controllers for Personal Data they submit or manage through the Platform. TradeEngine.AI acts as Data Processor when processing such data on the Customer's behalf, and as Data Controller for account administration, billing, security, analytics, and operational records.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">13.2 Customer Obligations</h3>
          <p className="text-gray-700 mb-2">As Data Controllers, Customers are responsible for:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Providing lawful grounds for processing and appropriate privacy notices</li>
            <li>Ensuring the accuracy and lawful submission of all data provided to the Platform</li>
            <li>Maintaining secure access controls and preventing unauthorized use</li>
            <li>Responding to End User data rights requests</li>
            <li>Implementing data minimization and timely deletion practices</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">13.3 Our Obligations as Data Processor</h3>
          <p className="text-gray-700 mb-2">When acting as Data Processor, TradeEngine.AI will:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>Process Customer Data only on documented instructions</li>
            <li>Maintain appropriate technical and organizational security measures</li>
            <li>Assist with data subject rights and regulatory obligations where applicable</li>
            <li>Notify Customers of Personal Data breaches without undue delay</li>
            <li>Return or delete Customer Data upon termination, subject to legal retention requirements</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">13.4 Sub-Processors and International Transfers</h3>
          <p className="text-gray-700 mb-4">
            Customers authorize the use of approved sub-processors and international transfers supported by Standard Contractual Clauses or other lawful safeguards. We will notify Customers of material updates to our sub-processor list.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">13.5 Compliance Information</h3>
          <p className="text-gray-700 mb-4">
            Customers may request information necessary to verify our compliance with applicable data protection laws and our Data Processing Agreement (DPA).
          </p>
          <p className="text-gray-700">
            For DPA requests or compliance questions, contact{" "}
            <a href="mailto:legal@tradeengine.ai" className="text-blue-600 hover:underline">legal@tradeengine.ai</a>.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. CHANGES TO THIS PRIVACY POLICY</h2>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Updates to the Policy</h3>
          <p className="text-gray-700 mb-4">
            We may update this Privacy Policy to reflect changes in our Services, business practices, or legal requirements. The "Last Updated" date will always indicate the latest revision.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Notice of Material Changes</h3>
          <p className="text-gray-700 mb-4">
            If we make material changes—such as expanding data categories, introducing new processing purposes, changing our sharing practices, or altering user rights—we will provide advance notice through email or in-platform notifications. In some cases, we may request your active consent where required by law.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Your Acceptance</h3>
          <p className="text-gray-700 mb-4">
            Your continued use of the Services after the updated Policy takes effect constitutes acceptance. If you do not agree to the changes, you may discontinue use of the Services before the effective date.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Version Control</h3>
          <p className="text-gray-700">
            We maintain dated versions of this Privacy Policy and make previous versions available upon request.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. CONTACT US AND DATA PROTECTION OFFICER</h2>
          <p className="text-gray-700 mb-4">
            If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">General Privacy Inquiries</h3>
              <div className="text-gray-700">
                <p className="font-semibold">TradeEngine.AI Pte. Ltd.</p>
                <p>[Business Address]</p>
                <p>[City, Postal Code]</p>
                <p>Singapore</p>
                <p className="mt-2">
                  <strong>Email:</strong>{" "}
                  <a href="mailto:privacy@tradeengine.ai" className="text-blue-600 hover:underline">
                    privacy@tradeengine.ai
                  </a>
                </p>
                <p>
                  <strong>Phone:</strong> [Phone Number]
                </p>
                <p>
                  <strong>Website:</strong>{" "}
                  <a href="http://www.tradeengine.ai" className="text-blue-600 hover:underline">
                    www.tradeengine.ai
                  </a>
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Protection Officer (DPO)</h3>
              <div className="text-gray-700">
                <p>
                  <strong>Name:</strong> [DPO Name]
                </p>
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:dpo@tradeengine.ai" className="text-blue-600 hover:underline">
                    dpo@tradeengine.ai
                  </a>
                </p>
                <p>
                  <strong>Phone:</strong> [DPO Phone Number]
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Singapore PDPC Contact</h3>
              <p className="text-gray-700 mb-2">
                For complaints or concerns about our data protection practices in Singapore:
              </p>
              <div className="text-gray-700">
                <p className="font-semibold">Personal Data Protection Commission (PDPC)</p>
                <p>10 Pasir Panjang Road #03-01</p>
                <p>Mapletree Business City</p>
                <p>Singapore 117438</p>
                <p className="mt-2">
                  <strong>Website:</strong>{" "}
                  <a
                    href="https://www.pdpc.gov.sg"
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.pdpc.gov.sg
                  </a>
                </p>
                <p>
                  <strong>Email:</strong> info@pdpc.gov.sg
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Response Times</h3>
          <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
            <li>We aim to respond to all inquiries within <strong>5 business days</strong></li>
            <li>Data subject rights requests processed according to timelines in Section 10</li>
            <li>Urgent security matters receive immediate attention</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Accessibility</h3>
          <p className="text-gray-700 mb-4">
            We are committed to making our Privacy Policy accessible. If you have difficulty accessing this Policy due to a disability, please contact us for assistance.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Language</h3>
          <p className="text-gray-700">
            This Privacy Policy is provided in English. If translated, the English version controls in case of conflicts.
          </p>
        </section>

        <div className="border-t-2 border-gray-200 pt-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">END OF PRIVACY POLICY</h2>
          
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">ACKNOWLEDGMENT</h3>
            <p className="text-gray-700">
              By using TradeEngine.AI Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
            </p>
          </div>

          <div className="text-center text-gray-600 space-y-1">
            <p>
              <strong>Last Updated:</strong> 01/01/2026
            </p>
            <p>
              <strong>Effective Date:</strong> 01/01/2026
            </p>
            <p>
              <strong>Version:</strong> 1.0
            </p>
          </div>

          <div className="text-center text-gray-700 mt-6">
            <p className="font-semibold">TradeEngine.AI Pte. Ltd.</p>
            <p>[Company Registration No.]</p>
            <p>[Business Address]</p>
            <p>Singapore</p>
            <p className="mt-4">
              <strong>Website:</strong>{" "}
              <a href="http://www.tradeengine.ai" className="text-blue-600 hover:underline">
                www.tradeengine.ai
              </a>
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:sales@tradeengine.ai" className="text-blue-600 hover:underline">
                sales@tradeengine.ai
              </a>
            </p>
            <p>
              <strong>Phone:</strong> +65 9004 2845
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;